/**
 * Centralized typography system for AcquireFlow
 * Ensures consistent heading sizes, fonts, and text styles across all components
 */

export const fontFamily = {
  heading: '"Clash Grotesk", "Clash Grotesk Placeholder", sans-serif',
  body: 'sans-serif',
  display: '"League Spartan", sans-serif', // Only for hero
} as const

export const headingSizes = {
  // Display heading (Hero only)
  display: 'text-6xl sm:text-7xl md:text-8xl lg:text-9xl',

  // Standard headings
  h1: 'text-5xl md:text-6xl lg:text-7xl', // Main section headings (48-72px)
  h2: 'text-4xl md:text-5xl lg:text-6xl', // Subsection headings (36-60px)
  h3: 'text-3xl md:text-4xl lg:text-5xl', // Card/component headings (30-48px)
  h4: 'text-2xl md:text-3xl', // Small headings (24-30px)
  h5: 'text-xl md:text-2xl', // Tiny headings (20-24px)
} as const

export const textSizes = {
  xl: 'text-xl md:text-2xl', // Large body text (20-24px)
  lg: 'text-lg md:text-xl', // Medium body text (18-20px)
  base: 'text-base md:text-lg', // Standard body text (16-18px)
  sm: 'text-sm md:text-base', // Small text (14-16px)
  xs: 'text-xs md:text-sm', // Extra small text (12-14px)
} as const

export const fontWeights = {
  black: 'font-black', // 900
  extrabold: 'font-extrabold', // 800
  bold: 'font-bold', // 700
  semibold: 'font-semibold', // 600
  medium: 'font-medium', // 500
  normal: 'font-normal', // 400
  light: 'font-light', // 300
} as const

export const letterSpacing = {
  tighter: 'tracking-tighter', // -0.05em
  tight: 'tracking-tight', // -0.025em
  normal: 'tracking-normal', // 0
  wide: 'tracking-wide', // 0.025em
  wider: 'tracking-wider', // 0.05em
  widest: 'tracking-widest', // 0.1em
} as const

export const lineHeight = {
  none: 'leading-none', // 1
  tight: 'leading-tight', // 1.25
  snug: 'leading-snug', // 1.375
  normal: 'leading-normal', // 1.5
  relaxed: 'leading-relaxed', // 1.625
  loose: 'leading-loose', // 2
} as const

/**
 * Pre-composed heading styles for consistent use
 */
export const headingStyles = {
  display: `${headingSizes.display} ${fontWeights.black} ${letterSpacing.tighter} ${lineHeight.none}`,
  h1: `${headingSizes.h1} ${fontWeights.bold} ${letterSpacing.tight} ${lineHeight.tight}`,
  h2: `${headingSizes.h2} ${fontWeights.bold} ${letterSpacing.tight} ${lineHeight.tight}`,
  h3: `${headingSizes.h3} ${fontWeights.semibold} ${letterSpacing.tight} ${lineHeight.snug}`,
  h4: `${headingSizes.h4} ${fontWeights.semibold} ${letterSpacing.normal} ${lineHeight.snug}`,
  h5: `${headingSizes.h5} ${fontWeights.medium} ${letterSpacing.normal} ${lineHeight.normal}`,
} as const

/**
 * Pre-composed body text styles
 */
export const bodyStyles = {
  xl: `${textSizes.xl} ${fontWeights.normal} ${lineHeight.relaxed}`,
  lg: `${textSizes.lg} ${fontWeights.normal} ${lineHeight.relaxed}`,
  base: `${textSizes.base} ${fontWeights.normal} ${lineHeight.normal}`,
  sm: `${textSizes.sm} ${fontWeights.normal} ${lineHeight.normal}`,
  xs: `${textSizes.xs} ${fontWeights.normal} ${lineHeight.normal}`,
} as const
